import React from 'react';
import { Helmet } from 'react-helmet-async';

export const TermsOfService = () => {
  return (
    <div className="pt-20">
      <Helmet>
        <title>Terms of Service | Trademark Infinite - Legal Terms & Conditions</title>
        <meta name="description" content="Review the terms of service for Trademark Infinite. Understand your rights and obligations when using our trademark registration and legal services." />
      </Helmet>
      <section className="bg-gradient-to-br from-red-600 to-violet-600 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center">Terms of Service</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">Last Updated: January 2026</p>

            <div className="space-y-8 text-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p>
                  Welcome to Trademark Infinite. By accessing or using our website and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
                <p>
                  Trademark Infinite provides trademark registration, copyright registration, company formation, and related intellectual property services. Our services include consultation, document preparation, filing assistance, and ongoing support for trademark and business protection matters.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Obligations</h2>
                <p className="mb-4">When using our services, you agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate, current, and complete information as requested</li>
                  <li>Maintain the confidentiality of your account credentials</li>
                  <li>Notify us immediately of any unauthorized use of your account</li>
                  <li>Use our services in compliance with all applicable laws and regulations</li>
                  <li>Not use our services for any unlawful or fraudulent purpose</li>
                  <li>Not interfere with or disrupt the integrity or performance of our services</li>
                  <li>Not attempt to gain unauthorized access to our systems or networks</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Service Fees and Payment</h2>
                <p className="mb-4">
                  Our services are provided for the fees stated on our website or as otherwise agreed in writing. Payment terms include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Service fees are separate from government filing fees</li>
                  <li>Payment is due at the time of service purchase unless otherwise agreed</li>
                  <li>All fees are non-refundable once services have been rendered</li>
                  <li>Government filing fees are non-refundable regardless of application outcome</li>
                  <li>We reserve the right to change our fees with notice to existing clients</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
                <p>
                  All content on our website, including text, graphics, logos, images, and software, is the property of Trademark Infinite or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from our content without our express written permission.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Service Limitations and Disclaimers</h2>
                <p className="mb-4">
                  We strive to provide excellent service, but certain limitations and disclaimers apply:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We do not guarantee approval of trademark applications by the USPTO</li>
                  <li>Application processing times are determined by government agencies, not us</li>
                  <li>We are not responsible for errors in information provided by you</li>
                  <li>Our services do not constitute legal advice unless explicitly stated</li>
                  <li>Results may vary based on individual circumstances and government decisions</li>
                </ul>
                <p className="mt-4">
                  OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, Trademark Infinite SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>Your use or inability to use our services</li>
                  <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
                  <li>Any interruption or cessation of transmission to or from our services</li>
                  <li>Any bugs, viruses, or similar issues transmitted through our services by third parties</li>
                  <li>Government rejection of trademark or other applications</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Indemnification</h2>
                <p>
                  You agree to indemnify, defend, and hold harmless Trademark Infinite, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with your access to or use of our services, your violation of these Terms, or your violation of any third-party rights.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Confidentiality</h2>
                <p>
                  We treat all client information as confidential and will not disclose your information to third parties except as necessary to provide our services, comply with legal obligations, or with your consent. You should review our Privacy Policy for more information about how we handle your data.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
                <p>
                  We reserve the right to terminate or suspend your access to our services at any time, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use our services will immediately cease.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law and Dispute Resolution</h2>
                <p className="mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of the United States and the State of New York, without regard to conflict of law provisions.
                </p>
                <p>
                  Any disputes arising from these Terms or your use of our services shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive relief in court for intellectual property matters.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on our website with a new "Last Updated" date. Your continued use of our services after such changes constitutes your acceptance of the new Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Severability</h2>
                <p>
                  If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Entire Agreement</h2>
                <p>
                  These Terms, together with our Privacy Policy, constitute the entire agreement between you and Trademark Infinite regarding the use of our services and supersede any prior agreements.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
                <p className="mb-4">If you have any questions about these Terms, please contact us at:</p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="font-semibold text-gray-900">Trademark Infinite</p>
                  <p>Email: info@trademarkinfinite.com</p>
                  <p>Phone: +1 (888) 201-6668</p>
                  <p>Address: One Wilshire Building, 624 S Grand Ave Floor 21, Los Angeles, CA 90017</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};